import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Check,
  Download,
  ChevronDown,
  ChevronUp,
  MapPin,
  Stethoscope,
  AlertCircle,
  CheckCircle,
  Loader2,
} from 'lucide-react';
import { clsx } from 'clsx';
import { api } from '../../lib/api';

// 6-dimension scoring configuration from Quality Assessment Criteria PDF
const QUALITY_DIMENSIONS = [
  {
    key: 'medicalFitScore' as const,
    label: 'A) Medical Fit',
    max: 3,
    description: 'Does the provider match the problem in terms of expertise?',
    criteria: [
      'Specialty (ENT, orthopedics, ophthalmology, internal medicine, etc.)',
      'Specialization (e.g., spine vs. general orthopedics)',
      'Setting (practice vs. center vs. emergency)',
    ],
    scores: [
      { value: 3, label: 'Exact match', description: 'e.g., knee sports injury → knee orthopedics/sports medicine' },
      { value: 2, label: 'Suitable', description: 'Basically suitable, but not optimally specialized' },
      { value: 1, label: 'Partially suitable', description: 'Relevant expertise lacking' },
      { value: 0, label: 'Wrong', description: 'Clearly wrong / risk of incorrect treatment' },
    ],
  },
  {
    key: 'urgencyTimingScore' as const,
    label: 'B) Urgency/Timing',
    max: 3,
    description: 'Was the patient referred at the right speed?',
    criteria: [
      'Red flags recognized?',
      'Deterioration / dynamics taken into account?',
      'Acute vs. chronic correctly classified?',
    ],
    scores: [
      { value: 3, label: 'Correct timing', description: 'Emergency/<24h/days/weeks as appropriate' },
      { value: 2, label: 'Slightly delayed', description: 'Slightly delayed or too early, but without risk' },
      { value: 1, label: 'Significantly wrong', description: 'Significantly too late or unnecessarily urgent' },
      { value: 0, label: 'Dangerous', description: 'Dangerously wrong (red flags overlooked / emergency missed)' },
    ],
  },
  {
    key: 'therapyPathwayScore' as const,
    label: 'C) Therapy Pathway',
    max: 3,
    description: 'Is the assignment embedded in a reasonable treatment pathway?',
    criteria: [
      'Clear pathway (conservative options → escalation → surgical indication)',
      'Appropriate sequencing (e.g., physical therapy before surgery)',
    ],
    scores: [
      { value: 3, label: 'Clear pathway', description: 'Conservative options exploited → escalation → surgical indication' },
      { value: 2, label: 'Unsystematic', description: 'Risk of "ping-pong" and inefficiency' },
      { value: 1, label: 'Wrong pathway', description: 'No escalation in case of complications/deterioration' },
    ],
  },
  {
    key: 'complexityRiskScore' as const,
    label: 'D) Complexity & Risk',
    max: 2,
    description: 'Was the patient sent to the right level of care according to risks?',
    criteria: [
      'Medications considered (anticoagulation/immunosuppression)',
      'Comorbidities addressed (well controlled vs. poorly controlled)',
      'Age/frailty factored in',
    ],
    scores: [
      { value: 2, label: 'Correctly prioritized', description: 'Comorbidities/medications considered and correctly prioritized' },
      { value: 1, label: 'Not prioritized', description: 'Considered but not correctly prioritized' },
      { value: 0, label: 'Not considered', description: 'Comorbidities/medications not considered' },
    ],
  },
  {
    key: 'outcomeFitScore' as const,
    label: 'E) Quality/Outcome Fit',
    max: 3,
    description: 'Is the service provider suitable for the desired outcome?',
    criteria: [
      'Specialization and subspecialization',
      'High number of cases for respective operations',
      'Low complication and revision rates',
      'Structured aftercare/rehabilitation possible',
    ],
    scores: [
      { value: 3, label: 'High outcome fit', description: 'Maximum fit of specialization + experience + low complications' },
      { value: 2, label: 'Generally good', description: 'Partially suitable, high case numbers, low complications' },
      { value: 1, label: 'Unclear fit', description: 'Specialization available but no figures' },
      { value: 0, label: 'Suboptimal risk', description: 'Incorrect specialization or no figures' },
    ],
  },
  {
    key: 'patientCentricityScore' as const,
    label: 'F) Patient Centricity',
    max: 2,
    description: 'Does the referral fit with patient goals and realities?',
    criteria: [
      'Return to sport / quality of life / freedom from pain',
      'Mobility, transport, care, understanding',
    ],
    scores: [
      { value: 2, label: 'Goals identified', description: 'Goals clearly identified and taken into account' },
      { value: 1, label: 'Partially considered', description: 'Partially taken into account' },
      { value: 0, label: 'Goals ignored', description: 'Goals ignored' },
    ],
  },
];

interface DimensionScores {
  medicalFitScore: number;
  urgencyTimingScore: number;
  therapyPathwayScore: number;
  complexityRiskScore: number;
  outcomeFitScore: number;
  patientCentricityScore: number;
}

interface ProviderAssessment {
  consolidatedProviderId: string;
  scores: DimensionScores;
  dimensionReasons: Record<string, string>;
  overallAssessment: string;
  totalScore: number;
  isGoodMatch: boolean;
  submitted?: boolean; // Track if assessment has been saved to backend
}

interface ProviderOriginalData {
  matchScore?: number;
  matchReasons?: string[];
  criteriaMatched?: string[];
  expertise?: string;
  specialty?: string;
  institution?: string;
}

interface ConsolidatedProvider {
  id: string;
  canonicalName: string;
  type: string;
  city: string | null;
  canton: string | null;
  specialty: string | null;
  recommendedByModels: string[];
  recommendationCount: number;
  avgRank: number;
  occurrences: number;
  allInstances: Array<{
    blindCode: string;
    searchModel: string;
    rank: number;
    originalData: ProviderOriginalData;
  }>;
}

function DimensionScoreInput({
  dimension,
  value,
  onChange,
  reason,
  onReasonChange,
  disabled,
}: {
  dimension: typeof QUALITY_DIMENSIONS[0];
  value: number;
  onChange: (value: number) => void;
  reason: string;
  onReasonChange: (reason: string) => void;
  disabled?: boolean;
}) {
  const [showHelp, setShowHelp] = useState(false);

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      <div className="flex items-start justify-between mb-2">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">{dimension.label}</h4>
          <p className="text-xs text-gray-600 mt-1">{dimension.description}</p>
        </div>
        <button
          onClick={() => setShowHelp(!showHelp)}
          className="text-xs text-blue-600 hover:text-blue-700 underline"
        >
          {showHelp ? 'Hide' : 'Show'} criteria
        </button>
      </div>

      {showHelp && (
        <div className="mb-3 p-2 bg-blue-50 rounded text-xs text-gray-700">
          <ul className="list-disc list-inside space-y-1">
            {dimension.criteria.map((criterion, idx) => (
              <li key={idx}>{criterion}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Score buttons */}
      <div className="grid gap-2 mb-3" style={{ gridTemplateColumns: `repeat(${dimension.scores.length}, minmax(0, 1fr))` }}>
        {dimension.scores.map((score) => (
          <button
            key={score.value}
            onClick={() => !disabled && onChange(score.value)}
            disabled={disabled}
            className={clsx(
              'px-3 py-3 rounded-lg text-xs font-medium transition-all border-2',
              value === score.value
                ? 'bg-amber-100 border-amber-500 text-amber-900 ring-2 ring-amber-200'
                : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-100',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
            title={score.description}
          >
            <div className="text-xl font-bold mb-1">{score.value}</div>
            <div className="line-clamp-2">{score.label}</div>
          </button>
        ))}
      </div>

      {/* Reasoning textarea */}
      <textarea
        value={reason}
        onChange={(e) => !disabled && onReasonChange(e.target.value)}
        placeholder="Explain your reasoning for this dimension..."
        disabled={disabled}
        rows={2}
        className={clsx(
          'w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none',
          disabled && 'bg-gray-50 cursor-not-allowed'
        )}
      />
    </div>
  );
}

function ProviderRationaleSection({ provider }: { provider: ConsolidatedProvider }) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Aggregate all unique match reasons from all instances
  const allMatchReasons = new Set<string>();
  const allCriteriaMatched = new Set<string>();
  let totalMatchScore = 0;
  let matchScoreCount = 0;

  provider.allInstances.forEach((instance) => {
    const data = instance.originalData;
    if (data.matchReasons) {
      data.matchReasons.forEach((reason: string) => allMatchReasons.add(reason));
    }
    if (data.criteriaMatched) {
      data.criteriaMatched.forEach((criterion: string) => allCriteriaMatched.add(criterion));
    }
    if (data.matchScore && data.matchScore > 0) {
      totalMatchScore += data.matchScore;
      matchScoreCount++;
    }
  });

  const avgMatchScore = matchScoreCount > 0 ? Math.round(totalMatchScore / matchScoreCount) : null;
  const hasRationale = allMatchReasons.size > 0 || allCriteriaMatched.size > 0 || avgMatchScore !== null;

  if (!hasRationale) {
    return null;
  }

  return (
    <div className="border border-blue-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-blue-100">
            <AlertCircle className="h-4 w-4 text-blue-600" />
          </div>
          <span className="text-sm font-semibold text-blue-900">Why This Provider?</span>
          {avgMatchScore !== null && (
            <span className={clsx(
              'ml-2 px-2 py-0.5 rounded-full text-xs font-bold',
              avgMatchScore >= 90 ? 'bg-green-100 text-green-700' :
              avgMatchScore >= 75 ? 'bg-blue-100 text-blue-700' :
              avgMatchScore >= 60 ? 'bg-yellow-100 text-yellow-700' :
              'bg-gray-100 text-gray-700'
            )}>
              Avg Match: {avgMatchScore}%
            </span>
          )}
        </div>
        <ChevronDown className={clsx('h-5 w-5 text-blue-600 transition-transform', isExpanded && 'rotate-180')} />
      </button>

      {isExpanded && (
        <div className="p-4 bg-white space-y-4">
          {/* Match Reasons */}
          {allMatchReasons.size > 0 && (
            <div>
              <h5 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                <CheckCircle className="h-3.5 w-3.5 text-blue-500" />
                Match Reasons
              </h5>
              <ul className="space-y-1.5">
                {Array.from(allMatchReasons).map((reason, i) => (
                  <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                    <span className="text-blue-500 mt-1">•</span>
                    <span>{reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Criteria Matched */}
          {allCriteriaMatched.size > 0 && (
            <div>
              <h5 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                <Check className="h-3.5 w-3.5 text-green-500" />
                Criteria Matched
              </h5>
              <div className="flex flex-wrap gap-1.5">
                {Array.from(allCriteriaMatched).map((criterion, i) => (
                  <span
                    key={i}
                    className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded-md border border-green-200"
                  >
                    {criterion}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Per-instance scores (without model names) */}
          {provider.allInstances.some(inst => inst.originalData.matchScore) && (
            <div>
              <h5 className="text-xs font-semibold text-gray-700 mb-2">Match Scores by Search Result</h5>
              <div className="flex flex-wrap gap-2">
                {provider.allInstances.map((instance, idx) => {
                  const score = instance.originalData.matchScore;
                  if (!score) return null;
                  return (
                    <div key={idx} className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded border border-gray-200">
                      <span className="font-mono text-xs text-gray-500">{instance.blindCode}</span>
                      <span className={clsx(
                        'text-xs font-bold',
                        score >= 90 ? 'text-green-600' :
                        score >= 75 ? 'text-blue-600' :
                        score >= 60 ? 'text-yellow-600' :
                        'text-gray-600'
                      )}>
                        {score}%
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProviderCard({
  provider,
  assessment,
  onAssessmentChange,
  onSubmit,
  isSubmitting,
}: {
  provider: ConsolidatedProvider;
  assessment: ProviderAssessment;
  onAssessmentChange: (assessment: ProviderAssessment) => void;
  onSubmit: () => Promise<void>;
  isSubmitting: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  const totalScore = Object.values(assessment.scores).reduce((sum, val) => sum + val, 0);
  const isComplete = totalScore > 0;
  const isSubmitted = assessment.submitted || false;

  const handleSubmit = async () => {
    await onSubmit();
    // Collapse card after successful save
    setExpanded(false);
  };

  // Calculate if good match (A+B+C+E must be ≥2, C must be ≥3)
  const isGoodMatch =
    assessment.scores.medicalFitScore >= 2 &&
    assessment.scores.urgencyTimingScore >= 2 &&
    assessment.scores.therapyPathwayScore >= 3 &&
    assessment.scores.outcomeFitScore >= 2;

  return (
    <div className={clsx(
      "border rounded-xl overflow-hidden bg-white shadow-sm transition-all",
      isSubmitted ? "border-green-300 bg-green-50" : "border-gray-200"
    )}>
      {/* Header */}
      <div className={clsx(
        "flex items-center justify-between p-4 border-b",
        isSubmitted ? "bg-green-50" : "bg-gray-50"
      )}>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900 text-lg">{provider.canonicalName}</h3>
            {isSubmitted ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-600 text-white rounded-full text-xs font-medium">
                <Check className="h-3 w-3" />
                Saved
              </span>
            ) : totalScore === 0 ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium border border-amber-300">
                <AlertCircle className="h-3 w-3" />
                Not Assessed
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Stethoscope className="h-3 w-3" />
              {provider.type}
            </span>
            {provider.canton && (
              <>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {provider.canton}
                </span>
              </>
            )}
            {provider.specialty && (
              <>
                <span>•</span>
                <span>{provider.specialty}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Models badge - hidden during quality assessment to maintain blind evaluation */}
          {/* Score badge */}
          {isComplete && (
            <div className="flex flex-col items-end">
              <div className={clsx(
                'px-3 py-1 rounded-full text-sm font-bold',
                isGoodMatch ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
              )}>
                {totalScore}/16
              </div>
              {isGoodMatch && (
                <span className="text-xs text-green-600 flex items-center gap-1 mt-1">
                  <CheckCircle className="h-3 w-3" />
                  Good Match
                </span>
              )}
            </div>
          )}
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title={isSubmitted ? "Click to edit" : "Click to expand"}
          >
            {expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="p-6 space-y-6">
          {/* Model recommendations hidden during quality assessment to maintain blind evaluation */}
          {/* Provider statistics (without revealing models) */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-700">
              Found in {provider.occurrences} search result{provider.occurrences !== 1 ? 's' : ''} • Avg rank: {provider.avgRank.toFixed(1)}
            </p>
          </div>

          {/* Why This Provider? - Aggregated rationale from all search results */}
          <ProviderRationaleSection provider={provider} />

          {/* Good Match Criteria Info */}
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <h5 className="text-xs font-semibold text-amber-900 mb-2">Good Match Criteria:</h5>
            <p className="text-xs text-amber-800">
              A + B + C + E must not be poor: A ≥ 2, B ≥ 2, C ≥ 3, E ≥ 2
            </p>
          </div>

          {/* Dimension scoring */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {QUALITY_DIMENSIONS.map((dimension) => (
              <DimensionScoreInput
                key={dimension.key}
                dimension={dimension}
                value={assessment.scores[dimension.key]}
                onChange={(value) =>
                  onAssessmentChange({
                    ...assessment,
                    scores: { ...assessment.scores, [dimension.key]: value },
                  })
                }
                reason={assessment.dimensionReasons[dimension.key] || ''}
                onReasonChange={(reason) =>
                  onAssessmentChange({
                    ...assessment,
                    dimensionReasons: { ...assessment.dimensionReasons, [dimension.key]: reason },
                  })
                }
              />
            ))}
          </div>

          {/* Overall assessment */}
          <div>
            <label className="text-sm font-semibold text-gray-900 mb-2 block">
              Overall Assessment (Optional)
            </label>
            <textarea
              value={assessment.overallAssessment}
              onChange={(e) =>
                onAssessmentChange({ ...assessment, overallAssessment: e.target.value })
              }
              placeholder="Provide an overall assessment of this provider for this patient case..."
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Provider instances (collapsible) - Always blind during quality assessment */}
          <details className="text-sm">
            <summary className="cursor-pointer text-gray-600 hover:text-gray-900 font-medium">
              View all {provider.allInstances.length} search result instances
            </summary>
            <div className="mt-3 space-y-2">
              {provider.allInstances.map((instance, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded border border-gray-100">
                  <div className="flex items-center justify-between mb-1">
                    <div>
                      <span className="font-mono text-xs text-gray-500">{instance.blindCode}</span>
                      {/* Model names are hidden during quality assessment to maintain blind evaluation */}
                    </div>
                    <span className="text-xs text-gray-500">Rank #{instance.rank}</span>
                  </div>
                  {instance.originalData.specialty && (
                    <p className="text-xs text-gray-600">{instance.originalData.specialty}</p>
                  )}
                </div>
              ))}
            </div>
          </details>

          {/* Submit button */}
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="text-sm text-gray-600">
              Total Score: <span className="font-bold text-lg">{totalScore}/16</span>
              <span className="ml-3">
                {isGoodMatch ? (
                  <span className="text-green-600 flex items-center gap-1">
                    <CheckCircle className="h-4 w-4" />
                    Good Match ✓
                  </span>
                ) : (
                  <span className="text-gray-500">Not a good match</span>
                )}
              </span>
            </div>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || totalScore === 0}
              className={clsx(
                'px-6 py-2 rounded-lg font-medium transition-colors flex items-center gap-2',
                isSubmitting || totalScore === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : isSubmitted
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-amber-600 text-white hover:bg-amber-700'
              )}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : isSubmitted ? (
                <>
                  <Check className="h-4 w-4" />
                  Update Assessment
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  Save Assessment
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function QualityAssessment() {
  const { id: testRunId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  console.log('[QualityAssessment] Component mounted');
  console.log('[QualityAssessment] testRunId:', testRunId);
  console.log('[QualityAssessment] Query enabled:', !!testRunId);

  const [assessments, setAssessments] = useState<Record<string, ProviderAssessment>>({});
  const [assessmentSetId, setAssessmentSetId] = useState<string | null>(null);

  // 1. Create assessment set and fetch providers
  const { data: setupData, isLoading: isLoadingSetup, error: setupError } = useQuery({
    queryKey: ['quality-assessment-setup', testRunId],
    queryFn: async () => {
      console.log('[QualityAssessment] queryFn STARTED');
      console.log('[QualityAssessment] Calling deduplicate-providers...');

      // First, deduplicate providers
      await api.post(`/api/validation/test-runs/${testRunId}/deduplicate-providers`, {});
      console.log('[QualityAssessment] Deduplication complete');

      console.log('[QualityAssessment] Creating/getting assessment set...');
      // Create or get existing assessment set (backend returns existing if found)
      const setResponse = await api.post(
        `/api/validation/test-runs/${testRunId}/quality-assessment-sets`,
        { topN: 10 }
      );
      console.log('[QualityAssessment] Assessment set response:', setResponse);

      const assessmentSetId = (setResponse as any).data.id;

      console.log('[QualityAssessment] Fetching full assessment set with existing assessments...');
      // Fetch the full assessment set including existing assessments
      const fullSetResponse = await api.get(
        `/api/validation/quality-assessment-sets/${assessmentSetId}`
      );
      console.log('[QualityAssessment] Full assessment set fetched:', fullSetResponse);
      console.log('[QualityAssessment] fullSetResponse.data:', (fullSetResponse as any).data);
      console.log('[QualityAssessment] Assessments array:', (fullSetResponse as any).data?.assessments);
      console.log('[QualityAssessment] Number of assessments:', (fullSetResponse as any).data?.assessments?.length || 0);

      console.log('[QualityAssessment] Fetching consolidated providers...');
      // Fetch consolidated providers
      const providersResponse = await api.get(
        `/api/validation/test-runs/${testRunId}/consolidated-providers?topN=10`
      );
      console.log('[QualityAssessment] Providers fetched:', providersResponse);

      return {
        assessmentSet: (fullSetResponse as any).data,
        providers: (providersResponse as any).data as ConsolidatedProvider[],
      };
    },
    enabled: !!testRunId,
    retry: false,
  });

  // Initialize assessments when providers load
  useEffect(() => {
    if (setupData?.providers && setupData?.assessmentSet) {
      setAssessmentSetId(setupData.assessmentSet.id);

      console.log('[QualityAssessment] Initializing assessments from backend data');
      console.log('[QualityAssessment] Existing assessments:', setupData.assessmentSet.assessments);

      // Create a map of existing assessments from the backend
      const existingAssessmentsMap = new Map<string, any>();
      if (setupData.assessmentSet.assessments) {
        setupData.assessmentSet.assessments.forEach((assessment: any) => {
          existingAssessmentsMap.set(assessment.consolidatedProviderId, assessment);
        });
      }

      const initialAssessments: Record<string, ProviderAssessment> = {};
      setupData.providers.forEach((provider) => {
        const existingAssessment = existingAssessmentsMap.get(provider.id);

        if (existingAssessment) {
          // Load existing assessment from backend
          console.log('[QualityAssessment] Loading existing assessment for provider:', provider.id);
          console.log('[QualityAssessment] Existing assessment totalScore:', existingAssessment.totalScore);

          // Only mark as submitted if it has actual scores (totalScore > 0)
          const hasActualScores = existingAssessment.totalScore > 0;

          initialAssessments[provider.id] = {
            consolidatedProviderId: provider.id,
            scores: {
              medicalFitScore: existingAssessment.medicalFitScore,
              urgencyTimingScore: existingAssessment.urgencyTimingScore,
              therapyPathwayScore: existingAssessment.therapyPathwayScore,
              complexityRiskScore: existingAssessment.complexityRiskScore,
              outcomeFitScore: existingAssessment.outcomeFitScore,
              patientCentricityScore: existingAssessment.patientCentricityScore,
            },
            dimensionReasons: existingAssessment.dimensionReasons || {},
            overallAssessment: existingAssessment.overallAssessment || '',
            totalScore: existingAssessment.totalScore,
            isGoodMatch: existingAssessment.isGoodMatch,
            submitted: hasActualScores, // Only mark as submitted if it has actual data
          };
        } else {
          // Initialize new assessment
          initialAssessments[provider.id] = {
            consolidatedProviderId: provider.id,
            scores: {
              medicalFitScore: 0,
              urgencyTimingScore: 0,
              therapyPathwayScore: 1,
              complexityRiskScore: 0,
              outcomeFitScore: 0,
              patientCentricityScore: 0,
            },
            dimensionReasons: {},
            overallAssessment: '',
            totalScore: 0,
            isGoodMatch: false,
            submitted: false,
          };
        }
      });

      console.log('[QualityAssessment] Initialized assessments:', initialAssessments);
      setAssessments(initialAssessments);
    }
  }, [setupData]);

  // 2. Submit individual assessment
  const submitAssessmentMutation = useMutation({
    mutationFn: async ({
      assessmentSetId: setId,
      providerId,
      assessment,
    }: {
      assessmentSetId: string;
      providerId: string;
      assessment: ProviderAssessment;
    }) => {
      console.log('[QualityAssessment] Submitting assessment for provider:', providerId, 'to set:', setId);
      const result = await api.post(
        `/api/validation/quality-assessment-sets/${setId}/providers/${providerId}`,
        {
          scores: assessment.scores,
          dimensionReasons: assessment.dimensionReasons,
          overallAssessment: assessment.overallAssessment,
        }
      );
      return { result, providerId };
    },
    onSuccess: (data) => {
      console.log('[QualityAssessment] Assessment saved successfully');
      const { providerId } = data;

      // Mark assessment as submitted
      setAssessments((prev) => ({
        ...prev,
        [providerId]: { ...prev[providerId], submitted: true },
      }));
    },
  });

  // 3. Complete assessment set
  const completeMutation = useMutation({
    mutationFn: async (setId: string) => {
      console.log('[QualityAssessment] Completing assessment set:', setId);
      const response = await api.post(`/api/validation/quality-assessment-sets/${setId}/complete`, {});
      console.log('[QualityAssessment] Complete response:', response);
      return response;
    },
    onSuccess: async () => {
      console.log('[QualityAssessment] Assessment set completed successfully');
      // Invalidate queries to refetch and show completed state
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['quality-assessment-setup', testRunId] }),
        // Also invalidate the test run query so the ValidationStepper sees the completed status
        queryClient.invalidateQueries({ queryKey: ['validation', 'test-run', testRunId] }),
        // And the test runs list for the progress indicator
        queryClient.invalidateQueries({ queryKey: ['validation', 'test-runs'] }),
      ]);
      console.log('[QualityAssessment] All queries invalidated, navigating back');
      navigate(`/validation/test-runs/${testRunId}`);
    },
    onError: (error) => {
      console.error('[QualityAssessment] Error completing assessment set:', error);
      alert('Failed to complete assessment. Please try again.');
    },
  });

  console.log('[QualityAssessment] isLoading:', isLoadingSetup);
  console.log('[QualityAssessment] error:', setupError);
  console.log('[QualityAssessment] data:', setupData);

  if (isLoadingSetup) {
    console.log('[QualityAssessment] Rendering loading state');
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  if (setupError) {
    console.log('[QualityAssessment] Rendering error state:', setupError);
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-red-900 mb-2">Failed to Load Quality Assessment</h2>
              <p className="text-sm text-red-700 mb-4">
                {setupError instanceof Error ? setupError.message : 'An error occurred while setting up the quality assessment'}
              </p>
              <p className="text-sm text-red-600 mb-4">
                This could happen if:
              </p>
              <ul className="text-sm text-red-600 list-disc list-inside space-y-1 mb-4">
                <li>Phase 2 (search) hasn't been completed yet</li>
                <li>No provider search results exist for this test run</li>
                <li>The search results don't contain provider data</li>
              </ul>
              <Link
                to={`/validation/test-runs/${testRunId}`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Test Run
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const providers = setupData?.providers || [];

  // Helper to calculate total score from scores object
  const calculateTotalScore = (scores: DimensionScores) =>
    Object.values(scores).reduce((sum, val) => sum + val, 0);

  // Count assessments that are both submitted AND have actual scores (totalScore > 0)
  // Note: We calculate from scores directly since totalScore in state may be stale
  const completedCount = Object.values(assessments).filter(a => {
    const calculatedTotal = calculateTotalScore(a.scores);
    return a.submitted === true && calculatedTotal > 0;
  }).length;

  const isCompleted = !!setupData?.assessmentSet?.completedAt;
  const allAssessmentsComplete = completedCount === providers.length;

  // Wait for assessments to be initialized
  const assessmentsInitialized = providers.length > 0 && Object.keys(assessments).length === providers.length;

  console.log('[QualityAssessment] Rendering main content');
  console.log('[QualityAssessment] providers.length:', providers.length);
  console.log('[QualityAssessment] assessments initialized:', assessmentsInitialized);
  console.log('[QualityAssessment] assessments keys:', Object.keys(assessments).length);
  console.log('[QualityAssessment] completedCount:', completedCount);
  console.log('[QualityAssessment] allAssessmentsComplete:', allAssessmentsComplete);
  console.log('[QualityAssessment] isCompleted:', isCompleted);
  console.log('[QualityAssessment] completedAt:', setupData?.assessmentSet?.completedAt);

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link
            to={`/validation/test-runs/${testRunId}`}
            className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1 mb-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Test Run
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Quality Assessment</h1>
          <p className="text-sm text-gray-600 mt-1">
            Evaluate top {providers.length} providers using 6-dimension criteria
          </p>
        </div>
        <div className="flex items-center gap-3">
          <a
            href={`/api/validation/quality-assessment-sets/${assessmentSetId}/export/pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Download className="h-4 w-4" />
            Export PDF
          </a>
          <button
            onClick={() => {
              if (!allAssessmentsComplete) {
                const incompleteProviders = providers
                  .filter(p => {
                    const assessment = assessments[p.id];
                    if (!assessment) return true;
                    const total = calculateTotalScore(assessment.scores);
                    return !assessment.submitted || total === 0;
                  })
                  .map(p => p.canonicalName)
                  .join(', ');
                alert(`Cannot complete assessment. The following providers have not been assessed:\n\n${incompleteProviders}`);
                return;
              }
              if (!assessmentSetId) {
                console.error('[QualityAssessment] Cannot complete: assessmentSetId is null');
                return;
              }
              completeMutation.mutate(assessmentSetId);
            }}
            disabled={isCompleted || !allAssessmentsComplete || completeMutation.isPending}
            className={clsx(
              'px-6 py-2 rounded-lg font-medium transition-colors',
              isCompleted
                ? 'bg-green-100 text-green-700 cursor-not-allowed border border-green-300'
                : !allAssessmentsComplete
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
            )}
            title={
              !allAssessmentsComplete && !isCompleted
                ? `${providers.length - completedCount} provider(s) still need to be assessed`
                : undefined
            }
          >
            {isCompleted ? (
              <>
                <CheckCircle className="h-4 w-4 inline mr-2" />
                Completed
              </>
            ) : completeMutation.isPending ? (
              'Completing...'
            ) : (
              'Complete Assessment'
            )}
          </button>
        </div>
      </div>

      {/* Completion Status Banner */}
      {isCompleted && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-green-900">Assessment Completed</h3>
              <p className="text-sm text-green-700 mt-1">
                This quality assessment was completed on {new Date(setupData.assessmentSet.completedAt).toLocaleString()}.
                All {providers.length} provider evaluations have been submitted.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Progress */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Progress</span>
          <span className="text-sm text-gray-600">
            {completedCount}/{providers.length} providers assessed
          </span>
        </div>
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-amber-600 transition-all duration-300"
            style={{ width: `${(completedCount / providers.length) * 100}%` }}
          />
        </div>

        {/* Show warning if not all assessments are complete */}
        {!isCompleted && !allAssessmentsComplete && (
          <div className="mt-3 flex items-start gap-2 text-sm text-amber-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>
              {providers.length - completedCount} provider(s) still need to be assessed before you can complete this assessment.
            </span>
          </div>
        )}
      </div>

      {/* Scoring Guide */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <h3 className="font-semibold text-blue-900 mb-3">Quality Assessment Framework</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 text-xs">
          {QUALITY_DIMENSIONS.map((dim) => (
            <div key={dim.key} className="bg-white p-3 rounded border border-blue-200">
              <div className="font-bold text-blue-900 mb-1">{dim.label}</div>
              <div className="text-gray-600">Max: {dim.max} points</div>
            </div>
          ))}
        </div>
        <div className="mt-3 text-sm text-blue-800">
          <AlertCircle className="h-4 w-4 inline mr-1" />
          Total possible: 16 points • Good Match requires: A≥2, B≥2, C≥3, E≥2
        </div>
      </div>

      {/* Provider Assessment Cards */}
      {!assessmentsInitialized ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
        </div>
      ) : (
        <div className="space-y-4">
          {providers.map((provider) => (
            <ProviderCard
              key={provider.id}
              provider={provider}
              assessment={assessments[provider.id]}
              onAssessmentChange={(updated) =>
                setAssessments((prev) => ({ ...prev, [provider.id]: updated }))
              }
              onSubmit={async () => {
                if (!assessmentSetId) {
                  console.error('[QualityAssessment] Cannot submit: assessmentSetId is null');
                  return;
                }
                await submitAssessmentMutation.mutateAsync({
                  assessmentSetId,
                  providerId: provider.id,
                  assessment: assessments[provider.id],
                });
              }}
              isSubmitting={submitAssessmentMutation.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}
